import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

// ─── Rich-toast types ────────────────────────────────────────────────────────
const TIER_COLORS = {
  light: '#a0aec0',
  medium: '#ecc94b',
  heavy: '#805ad5',
  ultra: '#ed8936',
};

const RICH_DURATION = { hours: 4000, xp: 3000, achievement: 5000, progress: 4500 };

// ─── Rendering helpers ───────────────────────────────────────────────────────
function HoursContent({ item }) {
  const accent = item.open ? '#48bb78' : '#718096';
  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--bg-card), var(--bg-secondary))',
      border: `2px solid ${accent}`,
      borderRadius: 'var(--radius)',
      padding: '12px 16px',
      maxWidth: 320,
      width: '100%',
      boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 16px ${accent}33`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 26, lineHeight: 1, filter: `drop-shadow(0 0 6px ${accent})` }}>
          {item.open ? '🌿' : '🔒'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: accent }}>
            {item.open ? "We're Open" : "We're Closed"}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {item.subtitle}
          </div>
        </div>
      </div>
    </div>
  );
}

function XpContent({ item }) {
  const isLevelUp = item.isLevelUp;
  const borderColor = isLevelUp ? '#ecc94b' : '#48bb78';
  const levelData = item.levelData;
  const progressPct = levelData ? Math.round(levelData.progress * 100) : 0;

  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--bg-card), var(--bg-secondary))',
      border: `2px solid ${borderColor}`,
      borderRadius: 'var(--radius)',
      padding: '12px 16px',
      maxWidth: 380,
      width: '100%',
      boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 16px ${borderColor}33`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 28, lineHeight: 1, filter: `drop-shadow(0 0 6px ${borderColor})` }}>
          {item.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isLevelUp ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#ecc94b', textTransform: 'uppercase', letterSpacing: 1 }}>
                Level Up!
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                {item.label}
              </div>
              {item.subtitle && (
                <div style={{ fontSize: 11, color: 'var(--accent)', fontStyle: 'italic', marginTop: 2 }}>
                  {item.subtitle}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#48bb78' }}>
                +{item.xp} XP
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {item.label}
              </div>
            </>
          )}
        </div>
        {levelData && (
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: `linear-gradient(135deg, ${borderColor}, ${isLevelUp ? '#d69e2e' : '#2f855a'})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, color: '#fff',
            boxShadow: `0 0 8px ${borderColor}66`,
          }}>
            {levelData.level}
          </div>
        )}
      </div>
      {levelData && !levelData.is_max_level && (
        <div style={{ marginTop: 8, height: 4, borderRadius: 2, background: 'var(--bg-secondary)', overflow: 'hidden' }}>
          <div style={{
            width: `${progressPct}%`,
            height: '100%', borderRadius: 2,
            background: `linear-gradient(90deg, ${borderColor}, #68d391)`,
            transition: 'width 0.8s ease',
          }} />
        </div>
      )}
    </div>
  );
}

function AchievementContent({ item }) {
  const tierColor = TIER_COLORS[item.tier] || TIER_COLORS.light;
  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--bg-card), var(--bg-secondary))',
      border: `2px solid ${tierColor}`,
      borderRadius: 'var(--radius)',
      padding: '14px 20px',
      maxWidth: 400,
      width: '100%',
      boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 20px ${tierColor}44`,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
    }}>
      <div style={{ fontSize: 32, lineHeight: 1, filter: `drop-shadow(0 0 6px ${tierColor})` }}>
        {item.icon || '⭐'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: tierColor, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
          Achievement Unlocked!
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
          {item.title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          +{item.reward_spins} spin{item.reward_spins > 1 ? 's' : ''} earned
        </div>
      </div>
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: tierColor,
        boxShadow: `0 0 8px ${tierColor}`,
        animation: 'achDotPulse 1s ease-in-out infinite',
      }} />
    </div>
  );
}

function ProgressContent({ item }) {
  const tierColor = TIER_COLORS[item.tier] || TIER_COLORS.light;
  const [barWidth, setBarWidth] = useState(item.fromPct);

  useEffect(() => {
    const t = setTimeout(() => setBarWidth(item.toPct), 80);
    return () => clearTimeout(t);
  }, [item.toPct]);

  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--bg-card), var(--bg-secondary))',
      border: `2px solid ${tierColor}`,
      borderRadius: 'var(--radius)',
      padding: '14px 20px',
      maxWidth: 400,
      width: '100%',
      boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 20px ${tierColor}33`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ fontSize: 30, lineHeight: 1, filter: `drop-shadow(0 0 6px ${tierColor})` }}>
          {item.icon || '⭐'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: tierColor, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
            Achievement Progress
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
            {item.title}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {item.progress} / {item.effective_requirement}
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: tierColor, minWidth: 36, textAlign: 'right' }}>
          {item.toPct}%
        </div>
      </div>
      <div style={{ marginTop: 10, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{
          width: `${barWidth}%`,
          height: '100%',
          borderRadius: 3,
          background: `linear-gradient(90deg, ${tierColor}cc, ${tierColor})`,
          transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: `0 0 8px ${tierColor}88`,
        }} />
      </div>
    </div>
  );
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function ToastProvider({ children }) {
  // ── Simple text toasts (stacked, for form feedback) ──
  const [simpleToasts, setSimpleToasts] = useState([]);

  const showToast = useCallback((type, text, duration = 3500) => {
    const id = Date.now() + Math.random();
    setSimpleToasts(prev => [...prev, { id, type, text }]);
    setTimeout(() => setSimpleToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  // ── Rich toast queue (one at a time) ──
  const [richCurrent, setRichCurrent] = useState(null);
  const [richExiting, setRichExiting] = useState(false);
  const richQueueRef = useRef([]);
  const richBusyRef = useRef(false);
  const richTimerRef = useRef(null);

  // Stored in a ref so recursive setTimeout always calls the latest version
  const advanceRef = useRef(null);
  advanceRef.current = () => {
    if (richQueueRef.current.length === 0) {
      richBusyRef.current = false;
      setRichCurrent(null);
      return;
    }
    const next = richQueueRef.current.shift();
    setRichCurrent(next);
    setRichExiting(false);
    richBusyRef.current = true;
    const duration = RICH_DURATION[next.type] || 4000;
    richTimerRef.current = setTimeout(() => {
      setRichExiting(true);
      richTimerRef.current = setTimeout(() => {
        setRichExiting(false);
        advanceRef.current();
      }, 400);
    }, duration);
  };

  const enqueueRich = useCallback((item) => {
    richQueueRef.current.push(item);
    if (!richBusyRef.current) advanceRef.current();
  }, []);

  const dismissRich = useCallback(() => {
    if (!richBusyRef.current) return;
    if (richTimerRef.current) clearTimeout(richTimerRef.current);
    setRichExiting(true);
    richTimerRef.current = setTimeout(() => {
      setRichExiting(false);
      advanceRef.current();
    }, 400);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, enqueueRich }}>
      {children}

      {/* Rich toast — one at a time */}
      {richCurrent && (
        <div
          onClick={dismissRich}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0,
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            padding: '12px 16px',
            pointerEvents: 'none',
            animation: richExiting
              ? 'achToastOut 0.4s ease forwards'
              : 'achToastIn 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <div style={{ pointerEvents: 'auto', cursor: 'pointer', width: '100%', display: 'flex', justifyContent: 'center' }}>
            {richCurrent.type === 'hours'       && <HoursContent item={richCurrent} />}
            {richCurrent.type === 'xp'          && <XpContent item={richCurrent} />}
            {richCurrent.type === 'achievement' && <AchievementContent item={richCurrent} />}
            {richCurrent.type === 'progress'    && <ProgressContent item={richCurrent} />}
          </div>
        </div>
      )}

      {/* Simple text toasts — stacked */}
      {simpleToasts.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          zIndex: 99998,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 6, padding: '12px 16px', pointerEvents: 'none',
        }}>
          {simpleToasts.map(t => (
            <div
              key={t.id}
              onClick={() => setSimpleToasts(prev => prev.filter(x => x.id !== t.id))}
              style={{
                pointerEvents: 'auto',
                maxWidth: 460, width: '100%',
                padding: '12px 18px',
                borderRadius: 8,
                background: t.type === 'success' ? 'rgba(20,52,36,0.97)' : 'rgba(52,20,20,0.97)',
                border: `1px solid ${t.type === 'success' ? 'var(--success)' : 'var(--danger)'}`,
                color: t.type === 'success' ? 'var(--success)' : 'var(--danger)',
                fontSize: 14, fontWeight: 500,
                boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                cursor: 'pointer',
                animation: 'toastSlideIn 0.3s cubic-bezier(0.34,1.56,0.64,1)',
              }}
            >
              {t.text}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx.showToast;
}

export function useEnqueueRich() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useEnqueueRich must be used within ToastProvider');
  return ctx.enqueueRich;
}
